/**
 * Process Registry (Standards)
 * Manages processes as Standards: templates, organization instances, policy framework link.
 * QMS Phase 1: clone templates, create template, standard–requirement link (StandardDetail).
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Archive } from 'lucide-react';
import { useProcesses, useProcessMutations } from '../../hooks/useProcesses';
import { useStandardTemplates, useQmsStandardMutations } from '../../hooks/useQmsStandards';
import { usePolicyFrameworks } from '../../hooks/usePolicyFrameworks';
import { usePolicyDocuments } from '../../hooks/useQmsPolicy';
import { useProcessCategories } from '../../hooks/useNomenclatures';
import { useNotifications } from '../NotificationSystem';
import Modal from '../ui/Modal';
import StandardDetail from './StandardDetail';
import './ProcessRegistry.css';

const ProcessRegistry = ({ organizationId, userId, userName, userRole }) => {
  const { t } = useTranslation(['ipc', 'qms']);
  const { processes, loading, error, refresh } = useProcesses(organizationId);
  const { templates, loading: templatesLoading, refresh: refreshTemplates } = useStandardTemplates();
  const { frameworks } = usePolicyFrameworks(organizationId);
  const { documents: policyDocuments } = usePolicyDocuments({ status: 'active' });
  const { items: processCategories, loading: categoriesLoading } = useProcessCategories(true);
  const { createProcess, updateProcess, deleteProcess, loading: mutating } = useProcessMutations();
  const { cloneTemplatesToOrganization, cloneSelectedTemplatesToOrganization, clearOrganizationStandards, loading: cloneLoading } = useQmsStandardMutations();
  const { notify } = useNotifications();

  const [tab, setTab] = useState('ourStandards'); // 'templates' | 'ourStandards'
  const [view, setView] = useState('list'); // 'list' | 'detail'
  const [selectedStandardId, setSelectedStandardId] = useState(null);
  const [selectedStandard, setSelectedStandard] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingProcess, setEditingProcess] = useState(null);
  const [isTemplateForm, setIsTemplateForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [processToDelete, setProcessToDelete] = useState(null);
  const [selectedFramework, setSelectedFramework] = useState('all');
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneModalPolicyDocId, setCloneModalPolicyDocId] = useState('');
  const [cloneModalSelectedIds, setCloneModalSelectedIds] = useState([]);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const [formData, setFormData] = useState({
    processCode: '',
    processName: '',
    processCategory: '',
    section: '',
    subsection: '',
    description: '',
    objective: '',
    scope: '',
    policyFrameworkId: '',
    policyDocumentId: '',
    isIPCRelevant: false,
    domain: '',
    regulatorySource: '',
  });

  const list = tab === 'templates' ? templates : processes;
  const listLoading = tab === 'templates' ? templatesLoading : loading;

  const filteredProcesses = useMemo(() => {
    const base = tab === 'templates' ? templates : processes;
    return selectedFramework === 'all'
      ? base
      : base.filter((p) => p.policyFrameworkId === selectedFramework);
  }, [tab, templates, processes, selectedFramework]);

  const handleCreate = () => {
    setEditingProcess(null);
    setIsTemplateForm(tab === 'templates');
    setFormData({
      processCode: '',
      processName: '',
      processCategory: '',
      section: '',
      subsection: '',
      description: '',
      objective: '',
      scope: '',
      policyFrameworkId: '',
      policyDocumentId: '',
      isIPCRelevant: false,
      domain: '',
      regulatorySource: '',
    });
    setShowForm(true);
  };

  const policyDocTitle = (id) => {
    if (!id) return '—';
    const doc = policyDocuments.find((d) => d.id === id);
    return doc ? (doc.title || doc.code) : null;
  };

  /** Document code (identifies the policy document, not the chapter/section title). */
  const policyDocCode = (id) => {
    if (!id) return '—';
    const doc = policyDocuments.find((d) => d.id === id);
    return doc ? doc.code : null;
  };

  const policyDocIdValid = (id) => policyDocuments.some((d) => d.id === id);

  const handleCloneTemplates = () => {
    setCloneModalPolicyDocId('');
    setCloneModalSelectedIds([]);
    setShowCloneModal(true);
  };

  /** Toate documentele de politică active (inclusiv cele fără șabloane încă). */
  const policyDocsForClone = useMemo(() => {
    const frameworksById = new Map(frameworks.map((fw) => [fw.id, fw]));
    const templateCountByDocId = templates.reduce((acc, t) => {
      const id = t.policyDocumentId;
      if (!id) return acc;
      acc[id] = (acc[id] || 0) + 1;
      return acc;
    }, {});

    return policyDocuments
      .map((doc) => {
        const frameworkName =
          (doc?.policyFrameworkId && frameworksById.get(doc.policyFrameworkId)?.name) ||
          doc?.policyFrameworkName ||
          '';
        return {
          id: doc.id,
          code: doc.code || doc.id,
          title: doc.title || doc.code || doc.id,
          frameworkName,
          templateCount: templateCountByDocId[doc.id] || 0,
        };
      })
      .sort((a, b) => String(a.code || '').localeCompare(String(b.code || '')));
  }, [templates, policyDocuments, frameworks]);

  const templatesForClonePolicyDoc = useMemo(() => {
    if (!cloneModalPolicyDocId) return [];
    return templates.filter((t) => t.policyDocumentId === cloneModalPolicyDocId);
  }, [templates, cloneModalPolicyDocId]);

  const handleCloneSelected = async () => {
    if (!organizationId || !cloneModalSelectedIds.length) return;
    try {
      await cloneSelectedTemplatesToOrganization(organizationId, cloneModalSelectedIds, userId, userName);
      notify.success(t('qms:cloneSuccess'));
      setShowCloneModal(false);
      setCloneModalSelectedIds([]);
      refresh();
      refreshTemplates();
    } catch (err) {
      notify.error(err.message || t('qms:saveFailed'));
    }
  };

  const handleClearOrgStandards = async () => {
    if (!organizationId) return;
    try {
      const { archived, deletedSr } = await clearOrganizationStandards(organizationId, userId, userName);
      notify.success(t('qms:clearOrgStandardsSuccess'));
      setShowClearConfirm(false);
      refresh();
    } catch (err) {
      notify.error(err.message || t('qms:saveFailed'));
    }
  };

  const handleOpenRequirements = (process) => {
    setSelectedStandardId(process.id);
    setSelectedStandard(process);
    setView('detail');
  };

  const handleEdit = (process, isTemplate = false) => {
    setEditingProcess(process);
    setIsTemplateForm(isTemplate);
    const policyDocumentId = process.policyDocumentId && policyDocIdValid(process.policyDocumentId)
      ? process.policyDocumentId
      : '';
    setFormData({
      processCode: process.processCode || '',
      processName: process.processName || '',
      processCategory: process.processCategory || '',
      section: process.section || '',
      subsection: process.subsection || '',
      description: process.description || '',
      objective: process.objective || '',
      scope: process.scope || '',
      policyFrameworkId: process.policyFrameworkId || '',
      policyDocumentId,
      isIPCRelevant: process.isIPCRelevant || false,
      domain: process.domain || '',
      regulatorySource: process.regulatorySource || '',
    });
    setShowForm(true);
  };

  const handleDelete = (processId) => {
    setProcessToDelete(processId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteProcess(processToDelete, userId, userName);
      notify.success(t('processArchived'));
      refresh();
      setShowDeleteConfirm(false);
      setProcessToDelete(null);
    } catch (err) {
      notify.error(err.message || t('failedToArchiveProcess'));
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.processCode || !formData.processName) {
        notify.error(t('processCodeNameRequired'));
        return;
      }

      if (editingProcess) {
        await updateProcess(editingProcess.id, formData, userId, userName);
        notify.success(t('processUpdated'));
      } else {
        const payload = isTemplateForm
          ? {
              ...formData,
              organizationId: null,
              isTemplate: true,
              domain: formData.domain || null,
              regulatorySource: formData.regulatorySource || null,
            }
          : { ...formData, organizationId };
        await createProcess(payload, userId, userName);
        notify.success(isTemplateForm ? t('qms:templateCreated') : t('processCreated'));
      }

      setShowForm(false);
      setEditingProcess(null);
      setIsTemplateForm(false);
      refresh();
      refreshTemplates();
    } catch (err) {
      notify.error(err.message || t('failedToSaveProcess'));
    }
  };

  if (view === 'detail' && selectedStandardId) {
    return (
      <StandardDetail
        standardId={selectedStandardId}
        standard={selectedStandard}
        onBack={() => {
          setView('list');
          setSelectedStandardId(null);
          setSelectedStandard(null);
        }}
        organizationId={organizationId}
        userId={userId}
        userName={userName}
        onStandardUpdated={(updated) => {
          setSelectedStandard(updated);
          refresh();
        }}
      />
    );
  }

  if (listLoading && filteredProcesses.length === 0) {
    return (
      <div className="process-registry">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('loadingProcesses')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="process-registry">
      <div className="manager-header manager-header-with-tabs">
        <h2>
          {t('processRegistry')}
          <span className="registry-total" title={tab === 'ourStandards' ? t('qms:ourStandards') : t('qms:templates')}>
            {' '}({filteredProcesses.length})
          </span>
        </h2>
        <div className="header-actions">
          {tab === 'ourStandards' && organizationId && (
            <>
              <button
                className="btn btn-danger-outline"
                onClick={() => setShowClearConfirm(true)}
                disabled={cloneLoading || processes.length === 0}
                title={t('qms:clearOrgStandards')}
              >
                {t('qms:clearOrgStandards')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCloneTemplates}
                disabled={cloneLoading}
              >
                {t('qms:cloneTemplatesToOrganization')}
              </button>
            </>
          )}
          {tab === 'templates' && userRole === 'management' && (
            <button className="btn btn-primary" onClick={handleCreate}>
              {t('qms:createTemplate')}
            </button>
          )}
          {tab === 'ourStandards' && (
            <button
              className="btn btn-primary"
              onClick={handleCreate}
              disabled={!organizationId}
            >
              {t('newProcess')}
            </button>
          )}
        </div>
      </div>

      <div className="standards-tabs">
        <button
          type="button"
          className={`tab-btn ${tab === 'ourStandards' ? 'active' : ''}`}
          onClick={() => setTab('ourStandards')}
        >
          {t('qms:ourStandards')}
        </button>
        <button
          type="button"
          className={`tab-btn ${tab === 'templates' ? 'active' : ''}`}
          onClick={() => setTab('templates')}
        >
          {t('qms:templates')}
        </button>
      </div>
      {!organizationId && tab === 'ourStandards' && (
        <div className="error-message" style={{ marginTop: '8px' }}>
          ⚠️ {t('noOrganizationProcess')}
        </div>
      )}

      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      <div className="filter-section">
        <label>{t('filterByFramework')}</label>
        <select
          value={selectedFramework}
          onChange={(e) => setSelectedFramework(e.target.value)}
          className="filter-select"
        >
          <option value="all">{t('allFrameworks')}</option>
          {frameworks.map((fw) => (
            <option key={fw.id} value={fw.id}>{fw.name}</option>
          ))}
        </select>
      </div>

      <div className="processes-list">
        {filteredProcesses.length === 0 ? (
          <div className="empty-state">
            <p>
              {tab === 'templates' ? t('qms:noTemplates') : t('qms:noStandardsInOrg')}
            </p>
          </div>
        ) : (
          <table className="processes-table">
            <thead>
              <tr>
                <th>{t('code')}</th>
                <th>{t('name')}</th>
                <th>{t('framework')}</th>
                <th>{t('qms:fromPolicyDocument')}</th>
                <th>{t('category')}</th>
                <th>{t('ipcRelevant')}</th>
                <th>{t('status')}</th>
                <th>{t('qms:manageRequirements')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredProcesses.map((process) => (
                <tr key={process.id}>
                  <td>{process.processCode || 'N/A'}</td>
                  <td className="process-name">{process.processName}</td>
                  <td>
                    {frameworks.find((fw) => fw.id === process.policyFrameworkId)?.name || 'N/A'}
                  </td>
                  <td className={policyDocCode(process.policyDocumentId) == null ? 'invalid-policy-ref' : ''}>
                    {policyDocCode(process.policyDocumentId) ?? policyDocTitle(process.policyDocumentId) ?? (process.policyDocumentId ? `${process.policyDocumentId} (${t('qms:documentNotFound')})` : '—')}
                  </td>
                  <td>{process.processCategory || 'N/A'}</td>
                  <td>
                    {process.isIPCRelevant ? (
                      <span className="badge badge-success">{t('yes')}</span>
                    ) : (
                      <span className="badge badge-secondary">{t('no')}</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge status-${process.status || 'active'}`}>
                      {process.status || 'active'}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-edit"
                      onClick={() => handleOpenRequirements(process)}
                    >
                      {t('qms:manageRequirements')}
                    </button>
                  </td>
                  <td>
                    <div className="actions">
                    {tab === 'ourStandards' && (
                      <>
                        <button
                          type="button"
                          className="btn-icon btn-icon-edit"
                          onClick={() => handleEdit(process)}
                          title={t('edit')}
                          aria-label={t('edit')}
                        >
                          <Pencil size={18} strokeWidth={2} />
                        </button>
                        <button
                          type="button"
                          className="btn-icon btn-icon-archive"
                          onClick={() => handleDelete(process.id)}
                          title={t('archive')}
                          aria-label={t('archive')}
                        >
                          <Archive size={18} strokeWidth={2} />
                        </button>
                      </>
                    )}
                    {tab === 'templates' && userRole === 'management' && (
                      <button
                        type="button"
                        className="btn-icon btn-icon-edit"
                        onClick={() => handleEdit(process, true)}
                        title={t('edit')}
                        aria-label={t('edit')}
                      >
                        <Pencil size={18} strokeWidth={2} />
                      </button>
                    )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingProcess(null);
        }}
        title={editingProcess ? t('editProcess') : t('newProcess')}
        size="large"
      >
        <div className="process-form">
          <div className="form-row">
            <div className="form-group">
              <label>
                {t('processCode')} <span className="required">*</span>
              </label>
              <input
                type="text"
                value={formData.processCode}
                onChange={(e) => setFormData({ ...formData, processCode: e.target.value })}
                placeholder={t('placeholderProcessCode')}
              />
            </div>

            <div className="form-group">
              <label>
                {t('processName')} <span className="required">*</span>
              </label>
              <input
                type="text"
                value={formData.processName}
                onChange={(e) => setFormData({ ...formData, processName: e.target.value })}
                placeholder={t('placeholderProcessName')}
              />
            </div>
          </div>

          <div className="form-group">
            <label>{t('policyFramework')}</label>
            <select
              value={formData.policyFrameworkId}
              onChange={(e) => setFormData({ ...formData, policyFrameworkId: e.target.value })}
            >
              <option value="">{t('selectFramework')}</option>
              {frameworks.map((fw) => (
                <option key={fw.id} value={fw.id}>{fw.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>{t('qms:fromPolicyDocument')}</label>
            <select
              value={formData.policyDocumentId || ''}
              onChange={(e) => setFormData({ ...formData, policyDocumentId: e.target.value || '' })}
            >
              <option value="">—</option>
              {policyDocuments.map((d) => (
                <option key={d.id} value={d.id}>{d.code} — {d.title}</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('section')}</label>
              <input
                type="text"
                value={formData.section}
                onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                placeholder={t('placeholderSection')}
              />
            </div>

            <div className="form-group">
              <label>{t('processCategory')}</label>
              {categoriesLoading ? (
                <div className="loading-state" style={{ padding: '8px', fontSize: '14px', color: '#718096' }}>
                  {t('loadingCategories')}
                </div>
              ) : (
                <select
                  value={formData.processCategory}
                  onChange={(e) => setFormData({ ...formData, processCategory: e.target.value })}
                  style={{ width: '100%', padding: '8px' }}
                >
                  <option value="">{t('selectCategory')}</option>
                  {processCategories.map(category => (
                    <option key={category.id} value={category.name}>
                      {category.name} {category.code ? `(${category.code})` : ''}
                    </option>
                  ))}
                </select>
              )}
              {processCategories.length === 0 && !categoriesLoading && (
                <div className="warning-message" style={{ marginTop: '8px', fontSize: '12px', padding: '8px', background: '#fef5e7', color: '#744210', borderRadius: '4px' }}>
                  ⚠️ {t('noProcessCategoriesFound')}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>{t('description')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder={t('placeholderDescribeProcess')}
            />
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isIPCRelevant}
                onChange={(e) => setFormData({ ...formData, isIPCRelevant: e.target.checked })}
              />
              {t('ipcRelevant')}
            </label>
          </div>

          {isTemplateForm && (
            <div className="form-row">
              <div className="form-group">
                <label>Domain</label>
                <input
                  type="text"
                  value={formData.domain || ''}
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                  placeholder="e.g. IPC"
                />
              </div>
              <div className="form-group">
                <label>Regulatory source</label>
                <input
                  type="text"
                  value={formData.regulatorySource || ''}
                  onChange={(e) => setFormData({ ...formData, regulatorySource: e.target.value })}
                  placeholder="e.g. ORDIN 1761/2022"
                />
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowForm(false);
                setEditingProcess(null);
              }}
            >
              {t('cancel')}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={mutating || !formData.processCode || !formData.processName}
            >
              {editingProcess ? t('saveChanges') : t('createProcess')}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setProcessToDelete(null);
        }}
        title={t('archiveProcess')}
        size="small"
      >
        <div className="delete-confirm">
          <p>{t('confirmArchiveProcess')}</p>
          <div className="form-actions">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowDeleteConfirm(false);
                setProcessToDelete(null);
              }}
            >
              {t('cancel')}
            </button>
            <button
              className="btn btn-danger"
              onClick={confirmDelete}
            >
              {t('archive')}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showCloneModal}
        onClose={() => {
          setShowCloneModal(false);
          setCloneModalPolicyDocId('');
          setCloneModalSelectedIds([]);
        }}
        title={t('qms:cloneByPolicyDocument')}
        size="large"
      >
        <div className="clone-modal">
          <div className="form-group">
            <label>{t('qms:selectPolicyDocument')}</label>
            <p className="clone-modal-hint">{t('qms:cloneModalFrameworkHint')}</p>
            <select
              value={cloneModalPolicyDocId}
              onChange={(e) => {
                setCloneModalPolicyDocId(e.target.value);
                setCloneModalSelectedIds([]);
              }}
              className="form-control"
            >
              <option value="">— {t('qms:selectPolicyDocument')} —</option>
              {policyDocsForClone.map((doc) => (
                <option key={doc.id} value={doc.id}>
                  {doc.code} — {doc.title}
                  {doc.frameworkName ? ` (${doc.frameworkName})` : ''}
                  {` [${doc.templateCount}]`}
                </option>
              ))}
            </select>
          </div>
          {cloneModalPolicyDocId && (
            <>
              <div className="form-group clone-modal-list-section">
                <label>{t('qms:selectTemplatesToClone')}</label>
                <div className="clone-modal-actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => setCloneModalSelectedIds(templatesForClonePolicyDoc.map((t) => t.id))}
                  >
                    {t('qms:selectAll')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => setCloneModalSelectedIds([])}
                  >
                    {t('qms:deselectAll')}
                  </button>
                </div>
                <ul className="clone-templates-list">
                  {templatesForClonePolicyDoc.map((t) => (
                    <li key={t.id}>
                      <label>
                        <input
                          type="checkbox"
                          checked={cloneModalSelectedIds.includes(t.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCloneModalSelectedIds((prev) => [...prev, t.id]);
                            } else {
                              setCloneModalSelectedIds((prev) => prev.filter((id) => id !== t.id));
                            }
                          }}
                        />
                        <span className="clone-template-code">{t.processCode}</span>
                        <span className="clone-template-name">{t.processName}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCloneModal(false);
                    setCloneModalPolicyDocId('');
                    setCloneModalSelectedIds([]);
                  }}
                >
                  {t('cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={cloneLoading || cloneModalSelectedIds.length === 0}
                  onClick={handleCloneSelected}
                >
                  {cloneLoading ? t('qms:loading') : `${t('qms:cloneSelected')} (${cloneModalSelectedIds.length})`}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        title={t('qms:clearOrgStandards')}
        size="small"
      >
        <div className="delete-confirm">
          <p>{t('qms:clearOrgStandardsConfirm')}</p>
          <div className="form-actions">
            <button
              className="btn btn-secondary"
              onClick={() => setShowClearConfirm(false)}
            >
              {t('cancel')}
            </button>
            <button
              className="btn btn-danger"
              onClick={handleClearOrgStandards}
              disabled={cloneLoading}
            >
              {t('qms:clearOrgStandards')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProcessRegistry;

